import { Alert, Icon, Text, Typography } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Pressable, StyleSheet, View } from 'react-native';
import { ToastOptions } from 'react-native-toast-notifications';

import { ISanityImage } from '@rbi-ctg/menu';
import LoyaltyPointsMedal from 'components/icons/loyalty-points-medal';
import Link from 'components/link';
import Picture from 'components/picture';
import { ISanityImage as GWISanityImage } from 'generated/graphql-gateway';
import { IImageFragment } from 'generated/sanity-graphql';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';
import { routes } from 'utils/routing';

import { modalToaster, rootToaster } from './refs';

const getToaster = () => modalToaster.current || rootToaster.current;
const DEFAULT_TOAST_DURATION = 3_000;
const LONG_TOAST_DURATION = 5_000;
// this is because a modal could be dismissing and we need the toast on the root view not on the now gone modal
const TOAST_DELAY = 200;
// applying defaults here because for some reason having multiple providers (as we need for modals) neither provider works for customization
// NOTE! there is also a patch-package file for this library for some other issues
const defaults: ToastOptions = {
  style: { backgroundColor: 'transparent', marginBottom: spacing.$2, padding: 0 },
  placement: 'top',
  duration: DEFAULT_TOAST_DURATION, // per design
};

export const StandardToast: React.FC<{
  text: string;
  variant: 'info' | 'success' | 'error' | 'neutral';
}> = ({ text, variant }) => (
  <Alert
    maxWidth="100%"
    alignSelf="center"
    flexDirection="row"
    status={variant || 'neutral'}
    variant="black"
    accessibilityLiveRegion="polite"
    accessibilityHint=""
    marginX={0}
    marginBottom={0}
    alignItems="center"
  >
    {variant !== 'neutral' && <Alert.Icon />}
    <Typography variant="copyOne" color="white" ml={variant === 'neutral' ? '$0' : '$2'}>
      {text}
    </Typography>
  </Alert>
);

export const addToCartToast = (
  message: string,
  image?: ISanityImage | IImageFragment | GWISanityImage | null,
  onRootToaster = true
) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={styles.addToCartWrap}>
        {image && (
          <View style={styles.pictureContainer}>
            <Picture alt={message.toString()} height="$12" width="$12" image={image} />
            <View style={styles.iconContainer}>
              <Icon variant="checkMarkGreen" size="$6" />
            </View>
          </View>
        )}
        <TextMessage>{message}</TextMessage>
      </View>,
      defaults
    );
  }, TOAST_DELAY);
};

const richToast = ({
  title,
  message,
  imageSource,
  options,
}: {
  title: string;
  message: string;
  imageSource?: any;
  options?: ToastOptions;
}) => {
  setTimeout(() => {
    (rootToaster.current || getToaster())?.show(
      <View style={styles.richToastWrap}>
        {imageSource && <Image alt={title} style={styles.richToastImage} source={imageSource} />}
        <View>
          <Text fontWeight="bold">{title}</Text>
          <Text>{message}</Text>
        </View>
      </View>,
      { ...defaults, ...options }
    );
  }, TOAST_DELAY);
};

let isAlertToastVisible = false;

const alertToast = ({
  title,
  message,
  learnMore,
  learnMoreUrl,
  options,
}: {
  title: string;
  message: string;
  learnMore?: string;
  learnMoreUrl?: string;
  options?: ToastOptions;
}) => {
  if (isAlertToastVisible) {
    return;
  }

  isAlertToastVisible = true;

  setTimeout(() => {
    (rootToaster.current || getToaster())?.show(
      <View style={alertToastStyles.container}>
        <View style={alertToastStyles.headerRow}>
          <Icon variant="info" size="$5" color={primitive.$white} />
          <Typography variant="copyOne" style={[alertToastStyles.title]}>
            {title}
          </Typography>
          <Pressable
            onPress={() => {
              isAlertToastVisible = false;
              (rootToaster.current || getToaster())?.hideAll();
            }}
            hitSlop={8}
          >
            <Icon variant="cancel" size="$5" color={primitive.$white} />
          </Pressable>
        </View>
        <Typography variant="copyOne" style={alertToastStyles.message}>
          {message}
        </Typography>
        {learnMoreUrl && learnMore && (
          <Link isInline to={learnMoreUrl}>
            <Text style={alertToastStyles.learnMore}>{learnMore}</Text>
          </Link>
        )}
      </View>,
      {
        ...defaults,
        ...options,
        style: alertToastStyles.default,
        onClose: () => {
          isAlertToastVisible = false;
        },
      }
    );
  }, TOAST_DELAY);
};

const celebrationToast = ({
  title,
  message,
  points,
  onRootToaster = true,
}: {
  title: string;
  message: string;
  points: number;
  onRootToaster?: boolean;
}) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={celebrationToastStyles.wrapper}>
        <View style={celebrationToastStyles.iconContainer}>
          <LoyaltyPointsMedal points={points} size={60} />
        </View>
        <View style={celebrationToastStyles.contentContainer}>
          <Text style={celebrationToastStyles.title}>{title}</Text>
          <Text style={celebrationToastStyles.message}>{message}</Text>
        </View>
      </View>,
      {
        ...defaults,
        style: celebrationToastStyles.default,
      }
    );
  }, TOAST_DELAY);
};

const viewAvailableOffersToast = (message: string, linkText: string = '', onRootToaster = true) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={styles.viewAvailableOffersWrap}>
        <Link isInline to={routes.offers}>
          <View>
            <Text variant="copyOne" fontWeight="bold">
              {message}
            </Text>
          </View>
          <View>
            <Text textDecorationLine="underline">{linkText}</Text>
          </View>
        </Link>
      </View>,
      // Waits until the store is fully
      { ...defaults, duration: LONG_TOAST_DURATION }
    );
  }, TOAST_DELAY);
};

const giftCardAddedToast = (onRootToaster = true) => {
  setTimeout(() => {
    (onRootToaster ? rootToaster.current : getToaster())?.show(
      <View style={giftCardToastStyles.container}>
        <View style={giftCardToastStyles.iconWrapper}>
          <Icon variant="checkFill" size="$5" color={primitive.$white} />
        </View>
        <Typography fontSize="sm" variant="copyOne" color="white" style={giftCardToastStyles.text}>
          <Typography variant="headerFourLook" color="white">
            {t('giftCardAdded', 'Gift card added!')}
          </Typography>{' '}
          {t(
            'useYourDigitalCardNumberForInStorePurchases',
            'Use your digital card number for in-store purchases.'
          )}
        </Typography>
      </View>,
      { ...defaults, duration: LONG_TOAST_DURATION, style: giftCardToastStyles.default }
    );
  }, TOAST_DELAY);
};

const showToast = (message: string, variant: 'info' | 'success' | 'error' | 'neutral') => {
  setTimeout(() => {
    getToaster()?.show(<StandardToast text={message} variant={variant} />, defaults);
  }, TOAST_DELAY);
};

export const toaster = {
  hideAll: () => getToaster()?.hideAll(),
  addToCartToast,
  celebrationToast,
  viewAvailableOffersToast,
  richToast,
  alertToast,
  giftCardAddedToast,
  informative: (message: string) => showToast(message, 'info'),
  positive: (message: string) => showToast(message, 'success'),
  negative: (message: string) => showToast(message, 'error'),
  neutral: (message: string) => showToast(message, 'neutral'),
};

const styles = StyleSheet.create({
  addToCartWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.$3,
    marginVertical: spacing.$1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: primitive.$blackOpacity4,
    elevation: 2,
  },
  richToastWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.$3,
    marginVertical: spacing.$1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    elevation: 2,
  },
  richToastImage: {
    height: 75,
    width: 75,
    marginRight: spacing.$3,
  },
  viewAvailableOffersWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    padding: spacing.$3,
    backgroundColor: primitive.$white,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: primitive.$bbqBrown,
    elevation: 2,
  },
  pictureContainer: {
    height: 60,
    width: 60,
    position: 'relative',
    borderWidth: 1,
    borderColor: primitive.$blackOpacity30,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    position: 'absolute',
    right: -4,
    top: -4,
    height: 15,
    width: 15,
  },
});

export const TextMessage = Text.withConfig({
  variant: 'copyOne',
  fontWeight: 'bold',
  flex: 1,
  marginLeft: spacing.$1,
});

const celebrationToastStyles = StyleSheet.create({
  default: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  wrapper: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    padding: 9,
    borderRadius: 10,
    borderWidth: 1,
    elevation: 2,
    borderColor: primitive.$bbqBrown,
    backgroundColor: primitive.$white,
  },
  title: {
    color: primitive.$bbqBrown,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: primitive.fontSizes.sm,
    fontWeight: 'normal',
  },
  message: {
    color: primitive.$bbqBrown,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 12,
    fontWeight: 'normal',
  },
  iconContainer: {
    marginHorizontal: 12,
  },
  contentContainer: {
    marginLeft: 12,
    display: 'flex',
    flexDirection: 'column',
  },
});

const alertToastStyles = StyleSheet.create({
  default: {
    backgroundColor: 'transparent',
    marginBottom: spacing.$2,
    padding: 0,
    margin: 0,
    justifyContent: 'center',
  },
  container: {
    backgroundColor: primitive.$bbqBrown,
    padding: 16,
    borderRadius: 12,
    minWidth: '100%',
    // To achieve full width toasts on mobile, increased the `maxWidth` to 120%.
    // If issues persist, we'll explore patching the library.
    width: isWeb ? '100%' : '120%',
    paddingHorizontal: isWeb ? 16 : 32,
    alignSelf: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  title: {
    color: primitive.$mayoEggWhite,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 22,
    flex: 1,
    flexShrink: 1,
  },
  message: {
    color: primitive.$mayoEggWhite,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 14,
    lineHeight: 18,
    marginBottom: 4,
    flexShrink: 1,
  },
  learnMore: {
    color: primitive.$white,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 14,
    textDecorationLine: 'underline',
    marginTop: 4,
  },
});

const giftCardToastStyles = StyleSheet.create({
  default: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: primitive.$black,
    padding: 16,
    gap: 8,
    borderRadius: 4,
    width: '100%',
  },
  iconWrapper: {
    padding: 2,
  },
  text: {
    flex: 1,
    flexShrink: 1,
    paddingRight: 16,
  },
});
